import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Mail, Link2, Unlink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, arabicError } from "../lib/api";

const ERR = {
  state: "انتهت صلاحية طلب الربط، حاول مجدداً",
  denied: "تم إلغاء الربط من صفحة Google",
  exchange: "تعذّر إتمام الربط مع Google، حاول مجدداً",
  scopes: "لم تُمنح الصلاحيات المطلوبة (إرسال وقراءة البريد)",
};

/** «ربط Gmail» status chip + connect/disconnect actions (tokens stay on the backend). */
const GmailConnect = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setStatus((await api.get("/oauth/gmail/status")).data); }
    catch { setStatus({ connected: false }); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const g = searchParams.get("gmail");
    if (!g) return;
    if (g === "connected") {
      toast.success("تم ربط البريد الإلكتروني بنجاح");
      try { localStorage.setItem("imdad_gmail_connected", String(Date.now())); } catch { /* ignore */ }
    } else toast.error(ERR[searchParams.get("reason")] || "تعذّر ربط Gmail");
    navigate("/messages/external-sourcing", { replace: true });
    load();
  }, [searchParams, navigate, load]);

  // The OAuth tab finishes elsewhere → refresh this tab's status (storage event, focus, and bounded polling while waiting)
  useEffect(() => {
    const onStorage = (e) => { if (e.key === "imdad_gmail_connected") { load(); setBusy(false); } };
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", load);
    return () => { window.removeEventListener("storage", onStorage); window.removeEventListener("focus", load); };
  }, [load]);
  useEffect(() => {
    if (!busy || status?.connected) return;
    let n = 0;
    const t = setInterval(async () => {
      n += 1;
      try {
        const s = (await api.get("/oauth/gmail/status")).data;
        if (s.connected) { setStatus(s); setBusy(false); toast.success("تم ربط البريد الإلكتروني بنجاح"); }
      } catch { /* keep waiting */ }
      if (n >= 100) setBusy(false);
    }, 3000);
    return () => clearInterval(t);
  }, [busy, status?.connected]);

  const connect = async () => {
    setBusy(true);
    // Google blocks its consent screen inside iframes (Emergent preview) → always leave the frame:
    // open a top-level tab synchronously (popup-blocker safe), then point it at the OAuth URL.
    const embedded = window.self !== window.top;
    const popup = window.open("", "_blank");
    try {
      const url = (await api.get("/oauth/gmail/start")).data.url;
      if (popup && !popup.closed) { popup.location.href = url; popup.focus?.(); return; }
      if (embedded) { window.top.location.href = url; return; }
      window.location.href = url;
    } catch (e) {
      popup?.close?.();
      toast.error(arabicError(e)); setBusy(false);
    }
  };
  const disconnect = async () => {
    if (!window.confirm("قطع ربط Gmail؟ لن يتمكن وكيل التوريد من استخدام بريدك بعد ذلك.")) return;
    setBusy(true);
    try { const r = await api.delete("/oauth/gmail"); toast.success(r.data.message); await load(); }
    catch (e) { toast.error(arabicError(e)); }
    finally { setBusy(false); }
  };

  if (!status) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="gmail-connect">
      {status.connected ? (
        <>
          <span className="h-10 px-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold flex items-center gap-2" data-testid="gmail-status-connected">
            <Mail size={15} /> تم ربط Gmail: <span dir="ltr">{status.email}</span>
          </span>
          <button onClick={disconnect} disabled={busy} data-testid="gmail-disconnect"
            className="h-10 px-3 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 text-xs font-bold flex items-center gap-1 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Unlink size={14} />} قطع الربط
          </button>
        </>
      ) : (
        <>
          <span className="text-xs text-[#666666] font-bold" data-testid="gmail-status-disconnected">البريد: غير مربوط</span>
          <button onClick={connect} disabled={busy} data-testid="gmail-connect-btn"
            className="h-10 px-4 rounded-lg bg-white border-2 border-[#09637E] text-[#09637E] hover:bg-[#EBF4F6] text-sm font-bold flex items-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Link2 size={16} />} {busy ? "أكمل الربط في نافذة Google الجديدة…" : "ربط Gmail"}
          </button>
        </>
      )}
    </div>
  );
};

export default GmailConnect;
